import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Lock, Loader2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// ResetPasswordPage - landing target for the reset link emailed to
// the user. Reads ?token=... from the URL, asks for a new password,
// POSTs to /api/auth/email/reset which both stores the new hash and
// issues a fresh session - so we redirect straight to /dashboard on
// success (no need to make the user log in again).
const ResetPasswordPage = () => {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { checkAuth } = useAuth();
    const token = useMemo(() => params.get('token') || '', [params]);

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
        if (password !== confirm) { setError('Passwords don\u2019t match.'); return; }
        setBusy(true);
        setError('');
        try {
            const res = await fetch('/api/auth/email/reset', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ token, password }),
            });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) {
                const detail = payload?.detail;
                throw new Error(typeof detail === 'string' ? detail : 'Could not reset password.');
            }
            setDone(true);
            toast.success('Password updated');
            await checkAuth({ silent: false, maxRetries: 1 });
            setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
        } catch (e2) {
            setError(e2.message || 'Could not reset password.');
        } finally {
            setBusy(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] p-6">
                <div className="max-w-md w-full bg-white rounded-2xl border border-zinc-200 p-7 text-center">
                    <KeyRound className="w-8 h-8 text-emerald-700 mx-auto mb-3" />
                    <h1 className="text-xl font-bold text-zinc-900 mb-1">Missing reset link</h1>
                    <p className="text-sm text-zinc-500 mb-5">This page needs a valid token. Request a new reset link from the sign-in screen.</p>
                    <Button onClick={() => navigate('/')} className="rounded-full">Back to home</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] p-6">
            <div className="max-w-md w-full bg-white rounded-2xl border border-zinc-200 p-7" data-testid="reset-password-page">
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
                    <KeyRound className="w-5 h-5 text-emerald-700" />
                </div>
                <h1 className="text-xl font-bold text-zinc-900 mb-1">Choose a new password</h1>
                <p className="text-sm text-zinc-500 mb-5">Pick something at least 8 characters. You\u2019ll be signed in automatically after.</p>

                {done ? (
                    <div className="text-center py-3" data-testid="reset-password-done">
                        <p className="text-emerald-700 font-semibold">Password updated. Redirecting\u2026</p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3">
                        <div className="relative">
                            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                            <Input
                                type="password"
                                placeholder="New password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="new-password"
                                minLength={8}
                                required
                                data-testid="reset-password-new-input"
                                className="pl-10 h-11 rounded-xl"
                            />
                        </div>
                        <div className="relative">
                            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                            <Input
                                type="password"
                                placeholder="Confirm new password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                autoComplete="new-password"
                                minLength={8}
                                required
                                data-testid="reset-password-confirm-input"
                                className="pl-10 h-11 rounded-xl"
                            />
                        </div>
                        {error && (
                            <div className="text-[12.5px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="reset-password-error" role="alert">
                                {error}
                            </div>
                        )}
                        <Button
                            type="submit"
                            disabled={busy || !password || !confirm}
                            className="w-full h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold mt-1"
                            data-testid="reset-password-submit"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update password'}
                        </Button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ResetPasswordPage;
